import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, ArrowRight, MapPin } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { getPackages, getQuote } from "@/lib/api";

const SUBURBS = [
  { name: "Maroochydore", km: 0 }, { name: "Alexandra Headland", km: 3 }, { name: "Mooloolaba", km: 5 },
  { name: "Buderim", km: 8 }, { name: "Kawana Waters", km: 10 }, { name: "Sippy Downs", km: 12 },
  { name: "Coolum Beach", km: 15 }, { name: "Nambour", km: 18 }, { name: "Caloundra", km: 20 },
  { name: "Peregian Beach", km: 22 }, { name: "Noosa Heads", km: 25 }, { name: "Montville", km: 30 },
  { name: "Maleny", km: 40 }, { name: "Gympie", km: 70 }, { name: "Brisbane", km: 100 },
];

export default function Quote() {
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(params.get("package") || "premium");
  const [guests, setGuests] = useState(40);
  const [travelKm, setTravelKm] = useState(20);
  const [suburb, setSuburb] = useState("Caloundra");
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    getPackages().then((d) => setPackages(d.packages));
  }, []);

  const recalc = useCallback(() => {
    getQuote({ package_id: selected, guest_count: guests, travel_km: travelKm }).then(setQuote).catch(() => {});
  }, [selected, guests, travelKm]);

  useEffect(() => { recalc(); }, [recalc]);

  const pickSuburb = (name) => {
    setSuburb(name);
    const s = SUBURBS.find((x) => x.name === name);
    if (s) setTravelKm(s.km);
  };

  const adjustKm = (v) => { setTravelKm(v); setSuburb("Custom distance"); };

  const activePkg = packages.find((p) => p.id === selected);

  return (
    <div className="bg-cream min-h-screen">
      <Nav solid />
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-32 md:pt-40 pb-24">
        <p className="overline text-xs text-coral mb-4">Quote Calculator</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ocean-deep leading-[1.02] tracking-tight max-w-3xl">
          Build your estimate in seconds.
        </h1>
        <p className="text-ocean/70 mt-4 max-w-xl">Choose a package and guest count for a live cost estimate. Final quotes are confirmed by enquiry.</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-14">
          <div className="lg:col-span-3 space-y-10 order-2 lg:order-1">
            <div>
              <h3 className="overline text-xs text-ocean mb-4">1 · Select a package</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {packages.map((p) => (
                  <button key={p.id} data-testid={`quote-pkg-${p.id}`} onClick={() => setSelected(p.id)}
                    className={`text-left p-5 rounded-2xl border transition-colors ${selected === p.id ? "bg-ocean-deep text-white border-ocean-deep" : "bg-white border-sand hover:border-coral"}`}>
                    <p className="font-serif text-2xl">{p.name}</p>
                    <p className={`text-xs mt-1 ${selected === p.id ? "text-white/70" : "text-ocean/60"}`}>{p.cocktails} cocktails · {p.duration}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-end mb-4">
                <h3 className="overline text-xs text-ocean">2 · Guest count</h3>
                <span className="font-serif text-4xl text-coral leading-none">{guests}</span>
              </div>
              <Slider data-testid="quote-guest-slider" value={[guests]} onValueChange={(v) => setGuests(v[0])} min={10} max={200} step={5} className="py-2" />
              <div className="flex justify-between text-xs text-ocean/50 mt-2"><span>10</span><span>200</span></div>
            </div>

            <div>
              <div className="flex justify-between items-end mb-4">
                <h3 className="overline text-xs text-ocean">3 · Your location</h3>
                <span className="font-serif text-3xl text-coral leading-none">{travelKm}km</span>
              </div>
              <Select value={SUBURBS.some((s) => s.name === suburb) ? suburb : ""} onValueChange={pickSuburb}>
                <SelectTrigger data-testid="quote-suburb" className="h-12 bg-white border-sand rounded-xl mb-4">
                  <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-coral" /><SelectValue placeholder="Select your suburb" /></span>
                </SelectTrigger>
                <SelectContent>
                  {SUBURBS.map((s) => (
                    <SelectItem key={s.name} value={s.name}>{s.name}{s.km > 40 ? " (travel fee applies)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Slider data-testid="quote-travel-slider" value={[travelKm]} onValueChange={(v) => adjustKm(v[0])} min={0} max={150} step={5} className="py-2" />
              <div className="flex justify-between text-xs text-ocean/50 mt-2"><span>0km</span><span>150km · fine-tune</span></div>
              <p className="text-xs text-ocean/60 mt-3">
                {travelKm <= 40
                  ? "Free travel — you're within our 40km coastal service zone."
                  : `Free within 40km · $2.50/km applies beyond (${travelKm - 40}km chargeable).`}
              </p>
            </div>

            <div>
              <h3 className="overline text-xs text-ocean mb-4">4 · What&apos;s included</h3>
              <div className="rounded-2xl border border-sand bg-white p-6">
                <p className="font-serif text-2xl text-ocean-deep mb-1">{activePkg?.name} Package</p>
                <p className="text-sm text-ocean/60 mb-5">{activePkg?.tagline}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    `${activePkg?.cocktails ?? ""} signature cocktails`,
                    activePkg?.duration ?? "",
                    "Dedicated professional bartender",
                    "Full mobile bar setup & tools",
                    "Bottled drinks & cans on request",
                    "Native botanical garnishes",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-ocean/80">
                      <Check className="w-4 h-4 mt-0.5 text-coral shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-ocean/50 mt-5 border-t border-sand pt-4">All bookings have a minimum charge equivalent to 20 guests.</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 order-1 lg:order-2">
            <motion.div layout className="sticky top-24 lg:top-28 rounded-3xl bg-ocean-deep text-white p-6 lg:p-8">
              <p className="overline text-xs text-coral mb-6">Your Estimate</p>
              {quote && (
                <>
                  <div className="space-y-3 mb-6 pb-6 border-b border-white/15">
                    {quote.breakdown.map((b, i) => (
                      <div key={i} className="flex justify-between text-sm text-white/80"><span>{b.label}</span><span>${b.amount.toLocaleString()}</span></div>
                    ))}
                  </div>
                  <div className="flex items-end justify-between mb-1">
                    <span className="overline text-xs text-white/70">Estimated total</span>
                    <span className="font-serif text-5xl text-coral leading-none" data-testid="quote-total">${quote.total.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-white/50 mb-6">≈ ${quote.per_guest_effective}/guest · {quote.at_minimum ? "20-guest minimum applied · " : ""}GST confirmed on enquiry</p>
                  <Link to={`/?package=${selected}&guests=${guests}&suburb=${encodeURIComponent(suburb)}&total=${quote.total}#contact`} data-testid="quote-book-btn" className="group flex items-center justify-between rounded-full bg-coral px-6 py-4 overline text-xs hover:bg-[#c9633f] transition-colors">
                    Enquire to Book <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
